/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Enum for message types displayed in the UI notification system.
 */
export enum MessageType {
  INFO = "info",
  SUCCESS = "success",
  WARN = "warning",
  ERROR = "error",
  DEFAULT = "default",
  LOADING = "loading",
}

/**
 * Interface for individual message state in the event notification system.
 */
export type MessageState = {
  showError?: boolean;
  message: string;
  messageType?: MessageType;
  code?: string;
  id: string; 
};

/**
 * Redux state for managing application-wide event messages and background process status.
 */
export type EventState = {
  messages: MessageState[];
  processRunning: boolean;
  isGlobalLoading: boolean;
};

const initialState: EventState = {
  messages: [],
  processRunning: false,
  isGlobalLoading: false,
};

/**
 * Adds an error message to the state if not already present.
 * Prevents duplicate error messages with same content.
 * @param state - Current event state
 * @param action - Payload containing error message data
 */
const addErrorMessage = (state: EventState, action: PayloadAction<MessageState>) => {
  if (
    state.messages.find(
      (mess) => mess.messageType === MessageType.ERROR && mess.message === action.payload.message
    ) === undefined
  ) {
    if (!action.payload.showError) {
      action.payload.showError = true;
    }
    action.payload.messageType = MessageType.ERROR;
    state.messages.push(action.payload);
  }
};

/**
 * Adds a success message to the state.
 * Always adds the message since each has a unique ID, duplicate prevention
 * is handled by notistack's preventDuplicate option.
 * @param state - Current event state
 * @param action - Payload containing success message data
 */
const addSuccessMessage = (state: EventState, action: PayloadAction<MessageState>) => {
  action.payload.messageType = MessageType.SUCCESS;
  state.messages.push(action.payload);
};

const addLoadingMessage = (state: EventState, action: PayloadAction<MessageState>) => {
  action.payload.messageType = MessageType.LOADING;
  state.messages.push(action.payload);
};

const addMessage = (state: EventState, action: PayloadAction<MessageState>) => {
  state.messages.push(action.payload);
};

const removeMessage = (state: EventState, action: PayloadAction<number>) => {
  state.messages.splice(action.payload, 1);
};

const resetErrorMessages = (state: EventState) => {
  state.messages = state.messages.filter((m) => m.messageType !== MessageType.ERROR);
};

const resetLoadingMessages = (state: EventState) => {
  state.messages = state.messages.filter((m) => m.messageType !== MessageType.LOADING);
};

/**
 * Clears all messages from the state.
 * @param state - Current event state
 */
const resetAllMessages = (state: EventState) => {
  state.messages = [];
};

const initBackgroundProcessFlag = (state: EventState) => {
  state.processRunning = true;
};

const stopBackgroundProcessFlag = (state: EventState) => {
  state.processRunning = false;
};

const setGlobalLoading = (state: EventState, action: PayloadAction<boolean>) => {
  state.isGlobalLoading = action.payload;
};

const EventSlice = createSlice({
  name: "error",
  initialState: initialState,
  reducers: {
    addErrorMessage: addErrorMessage,
    addMessage: addMessage,
    addSuccessMessage: addSuccessMessage,
    addLoadingMessage: addLoadingMessage,
    resetErrorMessages: resetErrorMessages,
    resetLoadingMessages: resetLoadingMessages,
    resetAllMessages: resetAllMessages,
    removeMessage: removeMessage,
    initProcess: initBackgroundProcessFlag,
    stopProcess: stopBackgroundProcessFlag,
    setGlobalLoading: setGlobalLoading,
  },
});

export default EventSlice;
